import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { HUBBELL_FEEDBACK_DIVISIONS } from '@erp/rpc';

import { triggerDownload } from 'src/lib/pricelist-utils';
import { parseCustomerList } from './parseCustomerList';
import { readLoads, readSpaList } from './readUnloadedSpaInputs';
import { buildUnloadedSpaReport } from './unloadedSpaReport';
import type { LoadLine, SpaListLine } from './unloadedSpaReport';
import { useUnloadedSpaLabels } from './useUnloadedSpaLabels';
import { writeUnloadedSpaWorkbook } from './writeUnloadedSpaWorkbook';

const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** A dropped input: being read, read into its lines, or not readable as that input. */
export type InputState<Line> =
  | { status: 'empty' }
  | { status: 'reading'; fileName: string }
  | { status: 'read'; fileName: string; lines: Line[] }
  | { status: 'failed'; fileName: string; error: unknown };

/** "N. Ontario (R)", or the bare letter for a tab the Division table lacks. */
export function divisionLabel(letter: string): string {
  const division = HUBBELL_FEEDBACK_DIVISIONS.find((d) => d.letter === letter);
  return division ? `${division.name} (${letter})` : letter;
}

/** The two files, read as they are dropped (only the latest drop of each lands). */
function useInput<Line>(read: (file: File) => Promise<Line[]>) {
  const [state, setState] = useState<InputState<Line>>({ status: 'empty' });
  const latest = useRef(0);
  async function drop(file: File): Promise<void> {
    latest.current += 1;
    const attempt = latest.current;
    setState({ status: 'reading', fileName: file.name });
    try {
      const lines = await read(file);
      if (attempt === latest.current) {
        setState({ status: 'read', fileName: file.name, lines });
      }
    } catch (error) {
      if (attempt === latest.current) {
        setState({ status: 'failed', fileName: file.name, error });
      }
    }
  }
  return { state, drop };
}

/**
 * The ABB Unloaded SPAs card: ABB's SPA list and the Results Workbook, the
 * Division to report on, an optional customer list, and the download. All of
 * it runs in the browser, like the rest of the page; nothing is stored.
 */
export function useUnloadedSpas() {
  const labels = useUnloadedSpaLabels();
  const spaList = useInput<SpaListLine>(readSpaList);
  const loads = useInput<LoadLine>(readLoads);
  const [pickedDivision, setDivision] = useState<string | null>(null);
  const [customerText, setCustomerText] = useState('');
  const [building, setBuilding] = useState(false);

  const divisions =
    loads.state.status === 'read'
      ? [...new Set(loads.state.lines.map((line) => line.division))].sort(
          (a, b) => divisionLabel(a).localeCompare(divisionLabel(b)),
        )
      : [];
  const division =
    pickedDivision !== null && divisions.includes(pickedDivision)
      ? pickedDivision
      : null;
  const customers = parseCustomerList(customerText);

  async function download(): Promise<void> {
    if (
      spaList.state.status !== 'read' ||
      loads.state.status !== 'read' ||
      division === null
    ) {
      return;
    }
    setBuilding(true);
    // No `finally`: the React Compiler cannot memoize a try/finally.
    try {
      const report = buildUnloadedSpaReport({
        spaLines: spaList.state.lines,
        loadLines: loads.state.lines,
        division,
        customers,
      });
      const bytes = await writeUnloadedSpaWorkbook({
        report,
        divisionName: divisionLabel(division),
        spaListFileName: spaList.state.fileName,
        resultsFileName: loads.state.fileName,
        withCustomers: customers.length > 0,
        labels,
      });
      const code =
        HUBBELL_FEEDBACK_DIVISIONS.find((d) => d.letter === division)?.code ??
        division;
      triggerDownload(
        new Blob([bytes], { type: XLSX_MIME_TYPE }),
        `ABB ${code} - SPAs Not Loaded.xlsx`,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    }
    setBuilding(false);
  }

  return {
    spaList: spaList.state,
    loads: loads.state,
    dropSpaList: (file: File) => void spaList.drop(file),
    dropLoads: (file: File) => void loads.drop(file),
    divisions,
    division,
    setDivision,
    customerText,
    setCustomerText,
    customerCount: customers.length,
    building,
    ready:
      spaList.state.status === 'read' &&
      loads.state.status === 'read' &&
      division !== null &&
      !building,
    download,
  };
}
